import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAppDispatch } from "@store/hooks";
import { setMfaPending } from "@store/slices/authSlice";
import { useNotification } from "@shared/contexts/NotificationContext";
import { redeemRecoveryCode } from "@services/api";
import {
  listMfaFactors,
  MfaFactor,
  signOut,
  supabase,
  verifyMfa,
} from "@services/supabaseAPI";

type Mode = "totp" | "recovery";

/**
 * The MFA challenge that renders inline in AuthenticationModal when
 * authSlice.mfaPending is true. Two paths to AAL2 / app entry:
 *
 *   1. TOTP — supabase.auth.mfa.challenge + verify against the
 *      enrolled factor. On success Supabase flips the session to
 *      aal2 and AuthContext's TOKEN_REFRESHED handler runs the
 *      bootstrap.
 *
 *   2. Recovery code — POST /auth/mfa/redeem-recovery-code. Server
 *      validates, marks the code used, deletes the user's TOTP
 *      factors, and wipes the remaining codes. We then refresh the
 *      session; the user is back at AAL1 with no factors, which
 *      satisfies every gate (RequireAAL2 will now reject them, but
 *      they have nothing protected by it until they re-enroll).
 *
 * Cancel signs the user out — there is no "skip MFA" path.
 */
const MfaChallengeForm = () => {
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useNotification();
  const [mode, setMode] = useState<Mode>("totp");
  const [factor, setFactor] = useState<MfaFactor | null>(null);
  const [loadingFactor, setLoadingFactor] = useState(true);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Guards both the listFactors() effect and the async submit handlers
  // from updating state on an unmounted component. Refs over flags so
  // the handlers don't need to be in dep arrays.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    (async () => {
      const factors = await listMfaFactors();
      if (!mountedRef.current) return;
      // We expect exactly one TOTP factor at this point — the user
      // got here because the AAL pre-flight saw next=aal2, which
      // requires at least one verified factor.
      setFactor(factors[0] ?? null);
      setLoadingFactor(false);
    })();
  }, []);

  const handleVerifyTotp = async () => {
    if (!factor) return;
    setSubmitting(true);
    setErrorMsg("");
    const result = await verifyMfa(factor.id, code.trim());
    if (!mountedRef.current) return;
    setSubmitting(false);
    if (!result.success) {
      setErrorMsg(result.error || "Incorrect code");
      setCode("");
      return;
    }
    // Supabase JS doesn't emit TOKEN_REFRESHED reliably after
    // mfa.verify, so we drive the AuthContext bootstrap explicitly
    // by clearing the pending flag. AuthContext watches the
    // true→false transition and runs the deferred bootstrap.
    dispatch(setMfaPending(false));
  };

  const handleRedeem = async () => {
    setSubmitting(true);
    setErrorMsg("");
    const result = await redeemRecoveryCode(recovery);
    if (!result.success) {
      if (!mountedRef.current) return;
      setErrorMsg(result.error.message || "Recovery code not recognised");
      setRecovery("");
      setSubmitting(false);
      return;
    }
    // Pull the new session state — factor list is now empty, AAL
    // gate no longer applies. Then clear mfaPending; AuthContext
    // catches the true→false transition and runs bootstrap. Don't
    // rely on TOKEN_REFRESHED firing — refreshSession's event
    // behavior varies across Supabase JS versions.
    await supabase.auth.refreshSession();
    if (!mountedRef.current) return;
    dispatch(setMfaPending(false));
    setSubmitting(false);
    showSuccess(
      "Recovery code used. Two-factor authentication has been removed — re-enroll from your profile when you're ready."
    );
  };

  const handleCancel = async () => {
    dispatch(setMfaPending(false));
    const result = await signOut();
    if (!mountedRef.current) return;
    if (!result.success) {
      showError(result.error || "Could not sign out");
    }
  };

  if (loadingFactor) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", py: 4 }}
        role="status"
        aria-label="Loading two-factor settings"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!factor && mode === "totp") {
    // Edge case: AAL pre-flight said step-up was needed but no factor
    // is enrolled. Should not happen in practice; surface the recovery
    // path so the user can at least reset rather than getting stuck.
    return (
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="warning">
          No authenticator was found on your account. Use a recovery code to
          sign in.
        </Alert>
        <Button
          variant="contained"
          onClick={() => setMode("recovery")}
          fullWidth
        >
          Use a recovery code
        </Button>
      </Stack>
    );
  }

  const canSubmit =
    !submitting &&
    (mode === "totp" ? code.length === 6 : recovery.trim().length > 0);

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {mode === "totp"
          ? "Enter the 6-digit code from your authenticator app."
          : "Paste one of the recovery codes you saved when you enabled two-factor. The code will be consumed and your authenticator will be reset."}
      </Typography>

      {mode === "totp" ? (
        <TextField
          autoFocus
          label="Authenticator code"
          value={code}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
          }
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            autoComplete: "one-time-code",
            "aria-label": "Authenticator code",
          }}
          disabled={submitting}
          error={!!errorMsg}
          helperText={errorMsg || " "}
          fullWidth
        />
      ) : (
        <TextField
          autoFocus
          label="Recovery code"
          placeholder="XXXX-XXXX-XXXX-XXXX"
          value={recovery}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setRecovery(e.target.value)
          }
          inputProps={{ "aria-label": "Recovery code" }}
          disabled={submitting}
          error={!!errorMsg}
          helperText={errorMsg || " "}
          fullWidth
        />
      )}

      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={mode === "totp" ? handleVerifyTotp : handleRedeem}
        disabled={!canSubmit}
        startIcon={
          submitting ? <CircularProgress size={18} color="inherit" /> : null
        }
      >
        {submitting
          ? "Verifying…"
          : mode === "totp"
            ? "Verify"
            : "Use recovery code"}
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => {
            setErrorMsg("");
            setMode(mode === "totp" ? "recovery" : "totp");
          }}
        >
          {mode === "totp" ? "Use a recovery code" : "Use my authenticator"}
        </Link>
        <Button onClick={handleCancel} disabled={submitting} size="small">
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default MfaChallengeForm;
